// SEO Meta Tag Injection - Fallback for failed SSR
// This utility detects when SSR meta tags are missing and injects them client-side

/**
 * Checks if SSR meta tags were successfully injected
 * @returns {boolean} true if SSR meta tags are present
 */
const hasSSRMetaTags = () => {
  // Check for our SSR-specific comment or Open Graph tags
  const hasSSRComment = document.documentElement.outerHTML.includes('<!-- Generated by Shop Worker -->');
  const hasOGTitle = document.querySelector('meta[property="og:title"]');
  
  // Check if content was injected between our placeholder comments
  const htmlContent = document.documentElement.outerHTML;
  const placeholderStart = htmlContent.indexOf('<!-- meta-tags-inject-placeholder-start -->');
  const placeholderEnd = htmlContent.indexOf('<!-- meta-tags-inject-placeholder-end -->');
  
  if (placeholderStart !== -1 && placeholderEnd !== -1) {
    const placeholderContent = htmlContent.substring(placeholderStart, placeholderEnd);
    // If there are actual meta tags (not just comments) between placeholders
    const hasMetaTagsInPlaceholder = placeholderContent.includes('<meta') || placeholderContent.includes('<title');
    if (hasMetaTagsInPlaceholder) {
      return true;
    }
  }
  
  return hasSSRComment || hasOGTitle;
};

/**
 * Removes any existing meta tag by name, property, or selector
 * @param {string} selector - CSS selector for the meta tag
 */
const removeExistingMeta = (selector) => {
  const existing = document.querySelector(selector);
  if (existing) {
    existing.remove();
  }
};

/**
 * Creates and appends a meta tag to the document head
 * @param {Object} attributes - Meta tag attributes
 */
const createMetaTag = (attributes) => {
  const meta = document.createElement('meta');
  Object.entries(attributes).forEach(([key, value]) => {
    meta.setAttribute(key, value);
  });
  document.head.appendChild(meta);
};

/**
 * Creates and appends a link tag to the document head
 * @param {Object} attributes - Link tag attributes
 */
const createLinkTag = (attributes) => {
  const link = document.createElement('link');
  Object.entries(attributes).forEach(([key, value]) => {
    link.setAttribute(key, value);
  });
  document.head.appendChild(link);
};

/**
 * Dynamically sets the page title with vendor branding
 * @param {Object} vendor - Vendor configuration object
 */
const setDynamicPageTitle = (vendor) => {
  const title = vendor.settings?.seo_title || vendor.name || 'Prepped';
  document.title = title;
};

/**
 * Dynamically sets the favicon from vendor configuration
 * @param {Object} vendor - Vendor configuration object
 */
const setDynamicFavicon = (vendor) => {
  // Priority order for favicon sources
  const faviconUrl = vendor.settings?.theme_mobile_logo_url || 
                     vendor.logo_image || 
                     vendor.settings?.theme_logo_url ||
                     '/favicon.ico';

  // Update the main favicon (id="favicon" from index.html)
  const mainFavicon = document.getElementById('favicon');
  if (mainFavicon) {
    mainFavicon.setAttribute('href', faviconUrl);
  }

  // Update or create shortcut icon link
  const shortcutIcon = document.querySelector('link[rel="shortcut icon"]');
  if (shortcutIcon) {
    shortcutIcon.setAttribute('href', faviconUrl);
  } else {
    createLinkTag({ rel: 'shortcut icon', href: faviconUrl, type: 'image/x-icon' });
  }

  // Update or create standard icon link
  const standardIcon = document.querySelector('link[rel="icon"]');
  if (standardIcon) {
    standardIcon.setAttribute('href', faviconUrl);
  } else {
    createLinkTag({ rel: 'icon', href: faviconUrl, type: 'image/x-icon' });
  }

  // Add apple-touch-icon if not already present
  if (!document.querySelector('link[rel="apple-touch-icon"]')) {
    createLinkTag({ rel: 'apple-touch-icon', href: faviconUrl });
  } else {
    // Update existing apple-touch-icon
    const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
    appleTouchIcon.setAttribute('href', faviconUrl);
  }
};

/**
 * Injects comprehensive SEO meta tags based on vendor configuration
 * @param {Object} vendor - Vendor configuration object
 * @param {string} domain - Current domain
 */
const injectSEOMetaTags = (vendor, domain) => {
  // Extract SEO data from vendor config
  const name = vendor.name || 'Prepped';
  const description = vendor.settings?.seo_description || 'Fresh, healthy meals delivered to your door';
  const title = vendor.settings?.seo_title || name;
  const logo = vendor.logo_image || vendor.settings?.theme_mobile_logo_url || '/favicon.ico';
  const url = domain;

  // CRITICAL: Set dynamic page title and favicon first
  setDynamicPageTitle(vendor);
  setDynamicFavicon(vendor);

  // Remove any existing meta tags that we're about to replace
  removeExistingMeta('meta[name="description"]');
  removeExistingMeta('meta[name="robots"]');
  removeExistingMeta('link[rel="canonical"]');
  
  // Open Graph tags
  removeExistingMeta('meta[property="og:type"]');
  removeExistingMeta('meta[property="og:url"]');
  removeExistingMeta('meta[property="og:title"]');
  removeExistingMeta('meta[property="og:description"]');
  removeExistingMeta('meta[property="og:image"]');
  removeExistingMeta('meta[property="og:image:width"]');
  removeExistingMeta('meta[property="og:image:height"]');
  removeExistingMeta('meta[property="og:image:alt"]');
  removeExistingMeta('meta[property="og:site_name"]');
  removeExistingMeta('meta[property="og:locale"]');
  
  // Twitter Card tags
  removeExistingMeta('meta[name="twitter:card"]');
  removeExistingMeta('meta[name="twitter:url"]');
  removeExistingMeta('meta[name="twitter:title"]');
  removeExistingMeta('meta[name="twitter:description"]');
  removeExistingMeta('meta[name="twitter:image"]');
  removeExistingMeta('meta[name="twitter:image:alt"]');
  
  // Additional SEO tags
  removeExistingMeta('meta[name="author"]');
  removeExistingMeta('meta[name="theme-color"]');
  removeExistingMeta('meta[name="msapplication-TileColor"]');
  removeExistingMeta('meta[name="application-name"]');

  // Basic SEO Meta Tags
  createMetaTag({ name: 'description', content: description });
  createMetaTag({ name: 'robots', content: 'index, follow' });
  
  // Canonical URL
  createLinkTag({ rel: 'canonical', href: url });

  // Open Graph / Facebook
  createMetaTag({ property: 'og:type', content: 'website' });
  createMetaTag({ property: 'og:url', content: url });
  createMetaTag({ property: 'og:title', content: title });
  createMetaTag({ property: 'og:description', content: description });
  createMetaTag({ property: 'og:image', content: logo });
  createMetaTag({ property: 'og:image:width', content: '1200' });
  createMetaTag({ property: 'og:image:height', content: '630' });
  createMetaTag({ property: 'og:image:alt', content: `${name} logo` });
  createMetaTag({ property: 'og:site_name', content: name });
  createMetaTag({ property: 'og:locale', content: 'en_US' });

  // Twitter Card
  createMetaTag({ name: 'twitter:card', content: 'summary_large_image' });
  createMetaTag({ name: 'twitter:url', content: url });
  createMetaTag({ name: 'twitter:title', content: title });
  createMetaTag({ name: 'twitter:description', content: description });
  createMetaTag({ name: 'twitter:image', content: logo });
  createMetaTag({ name: 'twitter:image:alt', content: `${name} logo` });

  // Additional SEO Meta
  createMetaTag({ name: 'author', content: name });
  createMetaTag({ name: 'theme-color', content: vendor.settings?.theme_color_1 || '#ffffff' });
  createMetaTag({ name: 'msapplication-TileColor', content: vendor.settings?.theme_color_1 || '#ffffff' });
  createMetaTag({ name: 'application-name', content: name });

  // JSON-LD Structured Data
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FoodEstablishment',
    name: name,
    description: description,
    url: url,
    logo: logo,
    image: logo,
    servesCuisine: 'Healthy',
    priceRange: '$$',
    serviceType: 'Meal Delivery',
  };

  // Remove existing JSON-LD if present
  const existingJsonLD = document.querySelector('script[type="application/ld+json"]');
  if (existingJsonLD) {
    existingJsonLD.remove();
  }

  // Add JSON-LD structured data
  const jsonLDScript = document.createElement('script');
  jsonLDScript.type = 'application/ld+json';
  jsonLDScript.textContent = JSON.stringify(structuredData);
  document.head.appendChild(jsonLDScript);

  // Add a comment to indicate client-side injection
  const comment = document.createComment('SEO Meta Tags injected client-side (SSR fallback)');
  document.head.appendChild(comment);
};

/**
 * Sets dynamic page title and favicon regardless of SSR status
 * This ensures branding is always applied from vendor configuration
 * @param {Object} vendor - Vendor configuration object
 */
export const setDynamicBranding = (vendor) => {
  if (!vendor) {
    return;
  }

  setDynamicPageTitle(vendor);
  setDynamicFavicon(vendor);
};

/**
 * Main function to check and inject SEO meta tags if needed
 * @param {Object} vendor - Vendor configuration object
 * @param {string} domain - Current domain (optional, defaults to window.origin)
 */
export const ensureSEOMetaTags = (vendor, domain = window.origin) => {
  // Only inject if vendor data is available
  if (!vendor) {
    return;
  }

  // ALWAYS set dynamic branding (title + favicon) regardless of SSR status
  setDynamicBranding(vendor);

  // Check if SSR meta tags are already present
  if (hasSSRMetaTags()) {
    return;
  }

  injectSEOMetaTags(vendor, domain);
};

export default ensureSEOMetaTags; 